import React, { useState } from "react";
import { ValueRange } from "./ValueRange.tsx";
import { PaperSettings, PaperAdvancedSettings } from "./PageSettings.tsx";
import { LongDivisionSettings } from "./LongDivisionSettings.tsx";
import { useSetting } from "./SettingsStore.tsx";
import { ProblemFormatOptions } from "../Constants.ts";
import { AdditionSubtractionSettings } from "./AdditionSubtractionSettings.tsx";
import { MissingNumberSettings } from "./MissingNumberSettings.tsx";
import { FormatSettings } from "./FormatSettings.tsx";
import { FractionSettings } from "./FractionSettings.tsx";
import { MoneySettings } from "./MoneySettings.tsx";
import { DecimalSettings } from "./DecimalSettings.tsx";
import { PlaceValueSettings } from "./PlaceValueSettings.tsx";
import { RoundingComparingSettings } from "./RoundingComparingSettings.tsx";
import { TimeSettings } from "./TimeSettings.tsx";
import { OrderOfOperationsSettings } from "./OrderOfOperationsSettings.tsx";
import { AlgebraSettings } from "./AlgebraSettings.tsx";
import { IntegerSettings } from "./IntegerSettings.tsx";
import { NumberPropertySettings } from "./NumberPropertySettings.tsx";
import { ConversionSettings } from "./ConversionSettings.tsx";
import { PatternSettings } from "./PatternSettings.tsx";
import { RomanSettings } from "./RomanSettings.tsx";
import { MagicSquareSettings } from "./MagicSquareSettings.tsx";
import { NumberLineSettings } from "./NumberLineSettings.tsx";
import { PercentSettings } from "./PercentSettings.tsx";
import { RatioSettings } from "./RatioSettings.tsx";
import { GeometrySettings } from "./GeometrySettings.tsx";
import { ShapeSettings } from "./ShapeSettings.tsx";
import { VolumeSettings } from "./VolumeSettings.tsx";
import { CircleSettings } from "./CircleSettings.tsx";
import { ProbabilitySettings } from "./ProbabilitySettings.tsx";
import { PythagoreanSettings } from "./PythagoreanSettings.tsx";
import { AverageSettings } from "./AverageSettings.tsx";
import { BarGraphSettings } from "./BarGraphSettings.tsx";
import { LinePlotSettings } from "./LinePlotSettings.tsx";
import { HistogramSettings } from "./HistogramSettings.tsx";
import { BoxPlotSettings } from "./BoxPlotSettings.tsx";
import { CoordinateSettings } from "./CoordinateSettings.tsx";
import { CalendarSettings } from "./CalendarSettings.tsx";
import { FactFamilySettings } from "./FactFamilySettings.tsx";
import { LanguageSettings } from "./LanguageSettings.tsx";
import { PresentationSettings } from "./PresentationSettings.tsx";
import { SpiralReviewSettings } from "./SpiralReviewSettings.tsx";
import { FactLevelSettings } from "./FactLevelSettings.tsx";
import { StoryTypeSettings } from "./StoryTypeSettings.tsx";
import {
    advancedSettingsInUse,
    showsFactLevels,
    showsLanguage,
    showsMissingNumber,
    showsRegrouping,
    showsRemainders,
    showsStoryTypes,
} from "./AdvancedSettings.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";

/**
 * One section of the settings rail (ADR 0061 §3): a hairline above every
 * section but the first, an optional kicker title with a one-line summary on
 * its right, and an `id` the mobile chip strip scrolls to.
 */
export function RailSection({
    id,
    title,
    summary,
    className,
    children,
}: {
    id?: string;
    title?: string;
    summary?: React.ReactNode;
    className?: string;
    children: React.ReactNode;
}) {
    const titleId = id && title ? `${id}-title` : undefined;
    return (
        <section id={id} className={'railSection' + (className ? ' ' + className : '')}
            aria-labelledby={titleId}>
            {title && (
                <div className='railSectionHead'>
                    <div id={titleId} className='kicker'>{title}</div>
                    {summary && <span className='railSummary'>{summary}</span>}
                </div>
            )}
            {children}
        </section>
    );
}

/**
 * The settings panel: every section of the rail below the operator bar.
 *
 * In order (ADR 0061 §3): the format picker; the format's own panel - the two
 * value ranges for arithmetic, or whichever panel replaces them; "Paper"; and
 * the advanced tier, collapsed behind one button at the foot.
 *
 * ADR 0023 §4 kept each advanced control where it applied - regrouping under
 * the operator bar, half page beside the page count - and hid them in place.
 * The workbench gathers them into one section instead, as the design has it: a
 * 308px rail with advanced controls scattered through it reads as the same
 * crowded panel 0023 set out to fix, and one block that opens and shuts is the
 * simpler thing to scan. §5 is unchanged: the block opens on load when an
 * advanced setting is doing something.
 *
 * The operator bar, the seed controls and the class roster are not in here:
 * the page places them - the rail's first section, the bar and strip around
 * the preview, and under the rail. See MathFactsGenerator.
 */
export const SettingsContainer = React.memo(function SettingsContainer() {
    // Only what decides which sections exist. The controls inside read their own
    // values, so a change to one of them does not redraw the panel (ADR 0081).
    const view = useSetting((settings) => ({
        format: settings.Format,
        factLevels: (settings.FactLevel ?? 0) > 0 && showsFactLevels(settings),
        inUse: advancedSettingsInUse(settings).length,
        remainders: showsRemainders(settings),
        regrouping: showsRegrouping(settings),
        missingNumber: showsMissingNumber(settings),
        storyTypes: showsStoryTypes(settings),
        language: showsLanguage(settings),
    }));
    const t = useTranslation();

    // Open on load when an advanced setting is already doing something, so a
    // route that pins one and a shared link that carries one both show the
    // control that explains the sheet (ADR 0023 §5). Read once: after that the
    // panel is the visitor's to open and close, and ticking "Timed test" must
    // not make the panel refuse to shut.
    const [showAdvanced, setShowAdvanced] = useState(
        () => view.inUse > 0
    );

    // Only shown collapsed, and only reachable by turning something on and then
    // hiding it - the rule above opens the panel in every other case. Without it
    // the sheet would quietly differ from what the visible controls describe.
    const inUse = view.inUse;
    const toggleLabel = inUse > 0
        ? format(t('settings.advancedOptionsOn'), { n: inUse })
        : t('settings.advancedOptions');

    return(
        <div className='settingsContainer'>
            <RailSection id='rail-format'>
                <FormatSettings/>
            </RailSection>
            {/* Value ranges pick digit buckets for whole numbers; a fraction
                sheet is sized by its denominators instead, so the two panels
                swap rather than both being shown. Whichever panel the format
                brings is basic, whole: without it the format has no controls
                at all (ADR 0023 §2). */}
            {formatPanel(view.format, view.factLevels)}
            <RailSection id='rail-paper' title={t('workbench.paper')}>
                <PaperSettings/>
            </RailSection>
            <RailSection id='rail-advanced' className={'railAdvanced' + (showAdvanced ? ' isOpen' : '')}>
                <div className='advancedHead'>
                    {showAdvanced && <div className='kicker'>{t('workbench.advanced')}</div>}
                    {/* One button, one boundary. Collapsed, it is a full-width
                        secondary button naming what it opens; open, it shrinks
                        to "hide" beside the section's kicker. The accessible
                        name says the whole thing either way. */}
                    <button
                        type='button'
                        className={showAdvanced ? 'advancedToggle advancedHide' : 'btn btn-secondary btn-block advancedToggle'}
                        aria-expanded={showAdvanced}
                        aria-controls={showAdvanced ? 'advancedBody' : undefined}
                        aria-label={showAdvanced ? t('settings.hideAdvancedOptions') : undefined}
                        onClick={() => setShowAdvanced(!showAdvanced)}
                    >
                        {showAdvanced ? t('workbench.hide') : toggleLabel}
                        <span className='advancedChevron' aria-hidden='true'>
                            {showAdvanced ? "▴" : "▾"}
                        </span>
                    </button>
                </div>
                {showAdvanced && (
                    <div id='advancedBody' className='advancedBody'>
                        {view.remainders && <LongDivisionSettings/>}
                        {view.regrouping && <AdditionSubtractionSettings/>}
                        {view.missingNumber && <MissingNumberSettings/>}
                        {view.storyTypes && <StoryTypeSettings/>}
                        {view.language && <LanguageSettings/>}
                        <PresentationSettings/>
                        <PaperAdvancedSettings/>
                    </div>
                )}
            </RailSection>
        </div>
    );
});

/**
 * The format's own panel, as rail sections. Arithmetic gets its two value
 * ranges, each a section of its own; a fact-family sheet gets its question
 * picker and the first range (the digit selection still applies, ADR 0020 §5);
 * every other format gets its one panel.
 *
 * A math-facts sheet with fact levels on gets the level picker in place of the
 * ranges (ADR 0071 §5): the level is how that sheet chooses its facts. The
 * ranges are kept in settings, not cleared, so they come back when levels go
 * off.
 */
function formatPanel(format: ProblemFormatOptions, factLevelsOn: boolean) {
    if (format === ProblemFormatOptions.MathFacts && factLevelsOn) {
        return (
            <RailSection id='rail-first' className='formatPanel'>
                <FactLevelSettings/>
            </RailSection>
        );
    }
    const Panel = FORMAT_PANELS[format];
    if (Panel) {
        return <RailSection id='rail-first' className='formatPanel'><Panel/></RailSection>;
    }
    if (format === ProblemFormatOptions.FactFamily) {
        return (
            <>
                <RailSection id='rail-first' className='formatPanel'>
                    <FactFamilySettings/>
                </RailSection>
                <RailSection id='rail-first-range'>
                    <ValueRange RangeSettings={0}/>
                </RailSection>
            </>
        );
    }
    return (
        <>
            <RailSection id='rail-first'>
                <ValueRange RangeSettings={0}/>
            </RailSection>
            <RailSection id='rail-second'>
                <ValueRange RangeSettings={1}/>
            </RailSection>
        </>
    );
}

/** Whether a format is sized by the two value ranges rather than a panel of its own. */
export function usesValueRanges(format: ProblemFormatOptions): boolean {
    return !FORMAT_PANELS[format];
}

const FORMAT_PANELS: Partial<Record<ProblemFormatOptions, React.ComponentType>> = {
    [ProblemFormatOptions.Fractions]: FractionSettings,
    [ProblemFormatOptions.Money]: MoneySettings,
    [ProblemFormatOptions.Decimals]: DecimalSettings,
    [ProblemFormatOptions.PlaceValue]: PlaceValueSettings,
    [ProblemFormatOptions.RoundingComparing]: RoundingComparingSettings,
    [ProblemFormatOptions.TellingTime]: TimeSettings,
    [ProblemFormatOptions.OrderOfOperations]: OrderOfOperationsSettings,
    [ProblemFormatOptions.Algebra]: AlgebraSettings,
    [ProblemFormatOptions.Integers]: IntegerSettings,
    [ProblemFormatOptions.NumberProperties]: NumberPropertySettings,
    [ProblemFormatOptions.UnitConversion]: ConversionSettings,
    [ProblemFormatOptions.NumberPatterns]: PatternSettings,
    [ProblemFormatOptions.RomanNumerals]: RomanSettings,
    [ProblemFormatOptions.MagicSquares]: MagicSquareSettings,
    [ProblemFormatOptions.NumberLine]: NumberLineSettings,
    [ProblemFormatOptions.Percent]: PercentSettings,
    [ProblemFormatOptions.Ratio]: RatioSettings,
    [ProblemFormatOptions.Shapes]: ShapeSettings,
    [ProblemFormatOptions.Volume]: VolumeSettings,
    [ProblemFormatOptions.Circles]: CircleSettings,
    [ProblemFormatOptions.Probability]: ProbabilitySettings,
    [ProblemFormatOptions.PythagoreanTheorem]: PythagoreanSettings,
    [ProblemFormatOptions.Geometry]: GeometrySettings,
    [ProblemFormatOptions.Averages]: AverageSettings,
    [ProblemFormatOptions.BarGraphs]: BarGraphSettings,
    [ProblemFormatOptions.LinePlots]: LinePlotSettings,
    [ProblemFormatOptions.Histograms]: HistogramSettings,
    [ProblemFormatOptions.BoxPlots]: BoxPlotSettings,
    [ProblemFormatOptions.CoordinatePlane]: CoordinateSettings,
    [ProblemFormatOptions.Calendar]: CalendarSettings,
    [ProblemFormatOptions.SpiralReview]: SpiralReviewSettings,
};
